import Decimal from 'decimal.js'
import type { CautionBancaire, Chantier } from '../db/modele'
import type { SituationLocal } from '../outbox/base'
import { cumulEffectif, derniereFinalisee } from './situations'
import { ajouterJours, ecartJours } from './planning'

// Libération de la retenue de garantie. Elle est prélevée sur chaque situation (jusqu'à 5 % du marché TTC) et
// libérée un an après la réception des travaux, sauf si des réserves ne sont pas levées (loi du 16 juillet 1971).
// Le suivi vit sur le chantier (réception, réserves, libération) parce qu'un devis signé ne se modifie plus.
// Ce n'est ni un avis juridique ni une caution bancaire (qui remplace la retenue : non gérée).

export const JOURS_AVANT_ECHEANCE = 30

/** Un an après la réception ; le 29 février d'une année bissextile devient le 1er mars (le jour d'après, jamais avant). */
export function echeanceRetenue(receptionLe: string): string {
  const [a, m, j] = receptionLe.split('-').map(Number) as [number, number, number]
  const suivant = `${String(a + 1).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(j).padStart(2, '0')}`
  const d = new Date(`${suivant}T00:00:00Z`)
  return d.getUTCMonth() + 1 === m ? suivant : ajouterJours(`${a + 1}-02-28`, 1) // 29/02 sans équivalent
}

export type EtatRetenue = 'liberee' | 'reserves' | 'echue' | 'bientot' | 'en_attente'

export type RetenueChantier = {
  chantierId: string
  chantierNom: string
  receptionLe: string
  echeance: string
  /** Retenue prélevée à date (TTC, avoirs partiels déduits). */
  montantTtc: Decimal
  etat: EtatRetenue
  /** Jours avant l'échéance (négatif si dépassée). */
  joursRestants: number
}

/** Retenues de garantie suivies : chantiers avec une réception saisie et une retenue prélevée. Triées par échéance. */
export function retenuesDeGarantie(chantiers: Pick<Chantier, 'id' | 'nom' | 'devisId' | 'receptionLe' | 'reserves' | 'retenueLibereeLe'>[], situationsDuDevis: (devisId: string) => SituationLocal[], aujourdhui: string): RetenueChantier[] {
  const lignes: RetenueChantier[] = []
  for (const c of chantiers) {
    if (!c.receptionLe || !c.devisId) continue
    const derniere = derniereFinalisee(situationsDuDevis(c.devisId))
    if (!derniere) continue
    const montantTtc = cumulEffectif(derniere).retenue.cumul
    if (!montantTtc.greaterThan(0)) continue
    const echeance = echeanceRetenue(c.receptionLe)
    const joursRestants = ecartJours(aujourdhui, echeance)
    const etat: EtatRetenue = c.retenueLibereeLe ? 'liberee' : c.reserves ? 'reserves' : joursRestants <= 0 ? 'echue' : joursRestants <= JOURS_AVANT_ECHEANCE ? 'bientot' : 'en_attente'
    lignes.push({ chantierId: c.id, chantierNom: c.nom, receptionLe: c.receptionLe, echeance, montantTtc, etat, joursRestants })
  }
  return lignes.sort((a, b) => a.echeance.localeCompare(b.echeance))
}

export const LIBELLES_ETAT_RETENUE: Record<EtatRetenue, string> = {
  liberee: 'Libérée',
  reserves: 'Réserves non levées : à ne pas réclamer',
  echue: 'À réclamer : le délai d’un an est écoulé',
  bientot: 'Bientôt libérable',
  en_attente: 'En attente du délai d’un an',
}

// -- Caution bancaire --------------------------------------------------------------------
// Le client peut accepter une caution à la place de la retenue de garantie. Ici seulement un suivi : montant, banque,
// référence et fin de validité. L'application ne vérifie pas l'acte de caution et ne retire pas la retenue des
// situations (le taux se règle à 0 % sur la situation).

export const JOURS_AVANT_FIN_CAUTION = 60

export type EtatCaution = 'active' | 'bientot' | 'expiree'

export function etatCaution(c: CautionBancaire, aujourdhui: string): { etat: EtatCaution; joursRestants: number | null } {
  if (!c.expireLe) return { etat: 'active', joursRestants: null }
  const joursRestants = ecartJours(aujourdhui, c.expireLe)
  return { etat: joursRestants < 0 ? 'expiree' : joursRestants <= JOURS_AVANT_FIN_CAUTION ? 'bientot' : 'active', joursRestants }
}

export const LIBELLES_ETAT_CAUTION: Record<EtatCaution, string> = {
  active: 'Caution valable',
  bientot: 'La caution arrive à échéance : à faire renouveler ou lever',
  expiree: 'Caution expirée : à renouveler, ou à faire lever si la retenue est libérée',
}

/** Cautions à surveiller : valables mais proches de leur fin, ou expirées. Rien pour une retenue déjà libérée. */
export function cautionsASurveiller(chantiers: Pick<Chantier, 'id' | 'nom' | 'caution' | 'retenueLibereeLe'>[], aujourdhui: string) {
  return chantiers
    .filter((c) => c.caution && !c.retenueLibereeLe)
    .map((c) => ({ chantierId: c.id, chantierNom: c.nom, caution: c.caution!, ...etatCaution(c.caution!, aujourdhui) }))
    .filter((c) => c.etat !== 'active')
    .sort((a, b) => (a.caution.expireLe ?? '').localeCompare(b.caution.expireLe ?? ''))
}
